import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, CircularProgress, Alert, Button, TextField, MenuItem, FormControlLabel, Checkbox,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import {
  fetchSkillSheetBank, assignSkillSheetToCourse, fetchChecklistsForCourse,
  fetchTestBank,
  fetchDocumentBank, assignDocumentToCourse,
} from "../api/backend";
import { assignBankTestAsClassLevel } from "../pages/courseHub/bankTestClone";
import { itemPoints } from "./checklistScoring";

const D = {
  ink: "var(--color-ink)", ink2: "var(--color-ink-2)", ink3: "var(--color-ink-3)", ink4: "var(--color-ink-4)",
  line: "var(--color-hairline-cloud)", line2: "var(--color-surface-press-light)", surface2: "var(--color-surface-2)", surface3: "var(--color-surface-press-light)",
  danger: "#B42318", danger50: "rgba(180,35,24,.08)",
};

const CONFIG = {
  "skill-sheets": {
    label: "Skill Sheet",
    plural: "Skill Sheets",
    fetch: q => fetchSkillSheetBank(q),
    assign: (courseId, item) => assignSkillSheetToCourse(courseId, { skillSheetId: item.id }),
    subtitle: sheetSummary,
  },
  tests: {
    label: "Test",
    plural: "Tests",
    fetch: q => fetchTestBank(q),
    // Clone into class-level course test (editable / movable), not a bank assignment join.
    assign: (courseId, item) => assignBankTestAsClassLevel(courseId, item),
    subtitle: item => item.passingScore ? `Passing: ${item.passingScore}%${item.maxRetakes ? ` · Retakes: ${item.maxRetakes}` : ""}` : null,
  },
  documents: {
    label: "Document",
    plural: "Documents",
    fetch: q => fetchDocumentBank(q),
    assign: (courseId, item) => assignDocumentToCourse(courseId, { documentId: item.id }),
    subtitle: item => item.contentType || null,
  },
};

function sheetItems(raw) {
  if (Array.isArray(raw)) return raw;
  try { const v = typeof raw === "string" || !raw ? JSON.parse(raw || "[]") : raw; return Array.isArray(v) ? v : []; } catch { return []; }
}

function sheetSummary(item) {
  const pts = sheetItems(item.items).reduce((n, i) => n + itemPoints(i), 0);
  return [
    item.passingScore ? `Passing: ${item.passingScore} pts` : null,
    pts ? `Total: ${pts} pts` : null,
    item.timeLimitMin ? `${item.timeLimitMin} min` : null,
    item.peerAssessmentRequired === false ? "Peer practice optional" : "Peer practice required",
  ].filter(Boolean).join(" · ");
}

function parseTags(raw = "[]") {
  try { const v = JSON.parse(raw); return Array.isArray(v) ? v : []; } catch { return raw.split(",").map(s => s.trim()).filter(Boolean); }
}

export default function AddFromBankModal({ open, onClose, courseId, type, onAdded, sessions = [], initialSessionId = null }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimary10 = alpha(tPrimary, 0.06);
  const tPrimaryDk = theme.palette.primary.dark;
  const tSecondary = theme.palette.secondary.main;
  const tSecondary10 = alpha(tSecondary, 0.08);
  const cfg = CONFIG[type] || CONFIG["skill-sheets"];
  const [items, setItems] = useState([]);
  const [destination, setDestination] = useState(initialSessionId == null ? "" : String(initialSessionId));
  const [enabled, setEnabled] = useState(true);
  const [requiredForPass, setRequiredForPass] = useState(false);
  const [draftCount, setDraftCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [err, setErr] = useState(null);
  const [successId, setSuccessId] = useState(null);
  const [addedIds, setAddedIds] = useState([]);
  const [notice, setNotice] = useState(null);

  const load = useCallback(() => {
    if (!open) return;
    setLoading(true);
    setErr(null);
    cfg.fetch(search || undefined)
      .then(d => {
        const all = Array.isArray(d) ? d : [];
        setItems(all.filter(item => item.isSystem || item.status === "PUBLISHED"));
        setDraftCount(all.filter(item => !item.isSystem && item.status !== "PUBLISHED").length);
        setLoading(false);
      })
      .catch((e) => { setItems([]); setErr(e?.response?.data?.message || "Failed to load bank items. Please retry."); setLoading(false); });
  }, [open, search, cfg]);

  useEffect(() => { load(); }, [load]);

  // Sheets already copied into this class (copy-on-assign keeps the bank id as provenance).
  useEffect(() => {
    if (!open || type !== "skill-sheets") return;
    Promise.resolve().then(() => fetchChecklistsForCourse(courseId))
      .then(d => setAddedIds((Array.isArray(d) ? d : []).map(c => c.sourceSkillSheetBankId).filter(Boolean)))
      .catch(() => {});
  }, [open, type, courseId]);

  useEffect(() => {
    if (!open) { setSearch(""); setSelected(null); setErr(null); setSuccessId(null); setNotice(null); }
  }, [open]);

  useEffect(() => {
    if (open) { setDestination(initialSessionId == null ? "" : String(initialSessionId)); setEnabled(true); setRequiredForPass(false); }
  }, [open, initialSessionId]);

  const handleAdd = async () => {
    if (!selected) return;
    setAssigning(true);
    setErr(null);
    try {
      const created = type === "skill-sheets"
        ? await assignSkillSheetToCourse(courseId, { skillSheetId: selected.id, sessionId: destination === "" ? null : Number(destination), enabled, requiredForPass })
        : await cfg.assign(courseId, selected);
      setSuccessId(selected.id);
      setAddedIds(ids => [...ids, selected.id]);
      setNotice(`"${selected.title}" added to this class.`);
      setSelected(null);
      try { await onAdded?.(created); } catch { setErr("The sheet was added, but the list could not refresh. Reload to see it."); }
      setTimeout(() => setSuccessId(null), 2000);
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to assign. Item may already be assigned.");
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: "20px 24px 14px", borderBottom: `1px solid ${D.line}` }}>
        <Box>
          <Box sx={{ fontSize: "17px", fontWeight: 800, color: D.ink, letterSpacing: "-.02em", fontFamily: "var(--font-display)" }}>Add from {cfg.plural} Bank</Box>
          <Box sx={{ fontSize: "12.5px", color: D.ink3, mt: "2px" }}>
            {type === "tests"
              ? "Copy into class-level tests (editable, movable to a session)"
              : "Pick an item to assign to this course"}
          </Box>
        </Box>
        <Box component="button" onClick={onClose} sx={{ width: 32, height: 32, borderRadius: "8px", border: `1.5px solid ${D.line}`, bgcolor: "transparent", display: "grid", placeItems: "center", cursor: "pointer", color: D.ink3, "&:hover": { bgcolor: D.surface3 } }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: "16px 24px", display: "flex", flexDirection: "column", gap: "12px" }}>
        <Box sx={{ bgcolor: D.surface2, border: `1px solid ${D.line}`, borderRadius: "12px", px: "14px", display: "flex", alignItems: "center", gap: "10px", height: 40 }}>
          <Box component="svg" width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={D.ink3} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </Box>
          <Box component="input" value={search} onChange={e => setSearch(e.target.value)} placeholder={`Search ${cfg.plural.toLowerCase()}…`}
            sx={{ flex: 1, border: 0, outline: 0, bgcolor: "transparent", fontSize: "13.5px", color: D.ink }} />
        </Box>

        {type === "skill-sheets" && <Box sx={{ display: "grid", gap: 1, mb: 2 }}>
          <TextField select label="Add to" value={destination} onChange={(e) => setDestination(e.target.value)} size="small"
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true, MenuProps: { transitionDuration: 0 } }}>
            <MenuItem value="">Course level</MenuItem>
            {sessions.map((s) => <MenuItem key={s.id} value={String(s.id)} disabled={Boolean(s.locked)}>{s.title || s.topic || 'Session ' + s.id}{s.locked ? ' (locked)' : ''}</MenuItem>)}
          </TextField>
          <FormControlLabel control={<Checkbox checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />} label="Available to learners" />
          <FormControlLabel control={<Checkbox checked={requiredForPass} onChange={(e) => setRequiredForPass(e.target.checked)} />} label="Required for course pass" />
        </Box>}
        {draftCount > 0 && <Alert severity="info" sx={{ mb: 1 }}>{draftCount} draft item{draftCount === 1 ? '' : 's'} hidden. Publish them in Knowledge Bank to add them here.</Alert>}
        {notice && <Alert severity="success" onClose={() => setNotice(null)} sx={{ borderRadius: "10px", py: "4px" }}>{notice}</Alert>}
        {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ borderRadius: "10px", py: "4px" }}>{err}<Button size="small" onClick={load}>Retry</Button></Alert>}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={28} sx={{ color: tPrimary }} /></Box>
        ) : items.length === 0 && !err ? (
          <Box sx={{ textAlign: "center", py: 4, color: D.ink4, fontSize: "13.5px" }}>{search ? `No ${cfg.plural.toLowerCase()} match.` : `Bank is empty.`}</Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: 380, overflowY: "auto", pr: "4px" }}>
            {items.map(item => {
              const isSelected = selected?.id === item.id;
              const isSuccess = successId === item.id;
              const isAdded = type === "skill-sheets" && addedIds.includes(item.id);
              return (
                <Box key={item.id}
                  onClick={() => !isAdded && setSelected(isSelected ? null : item)}
                  sx={{
                    p: "12px 14px", borderRadius: "12px", cursor: isAdded ? "default" : "pointer", opacity: isAdded ? 0.6 : 1,
                    border: `1.5px solid ${isSelected ? tPrimary : isSuccess ? tSecondary : D.line}`,
                    bgcolor: isSelected ? tPrimary10 : isSuccess ? tSecondary10 : "#fff",
                    transition: "all .12s",
                    "&:hover": { borderColor: isSuccess ? tSecondary : tPrimary, bgcolor: isSelected ? tPrimary10 : D.surface2 },
                  }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px" }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ fontSize: "14px", fontWeight: 700, color: isSelected ? tPrimary : D.ink, mb: "2px" }}>{item.title}</Box>
                      {item.description && <Box sx={{ fontSize: "12.5px", color: D.ink3, mb: "4px", lineHeight: 1.4 }}>{item.description}</Box>}
                      <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                        {cfg.subtitle(item) && <Box sx={{ fontSize: "11.5px", color: D.ink4 }}>{cfg.subtitle(item)}</Box>}
                        {parseTags(item.tags).map(t => (
                          <Box key={t} component="span" sx={{ fontSize: "10.5px", fontWeight: 700, px: "7px", py: "2px", borderRadius: "999px", bgcolor: tPrimary10, color: tPrimary }}>{t}</Box>
                        ))}
                      </Box>
                    </Box>
                    {isAdded && <Box component="span" sx={{ fontSize: "11px", fontWeight: 700, color: tSecondary, flexShrink: 0 }}>Added</Box>}
                    {isSuccess && (
                      <Box sx={{ color: tSecondary, flexShrink: 0 }}>
                        <Box component="svg" width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </Box>
                      </Box>
                    )}
                    {isSelected && !isSuccess && (
                      <Box sx={{ width: 18, height: 18, borderRadius: "50%", bgcolor: tPrimary, display: "grid", placeItems: "center", flexShrink: 0 }}>
                        <Box component="svg" width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: "14px 24px", borderTop: `1px solid ${D.line}`, gap: "9px", justifyContent: "flex-end" }}>
        <Box component="button" onClick={onClose} sx={{ height: 36, px: "16px", borderRadius: "10px", border: `1.5px solid ${D.line}`, bgcolor: "transparent", fontFamily: "inherit", fontSize: "13px", fontWeight: 700, color: D.ink2, cursor: "pointer", "&:hover": { bgcolor: D.surface3 } }}>
          Done
        </Box>
        <Box component="button" onClick={handleAdd} disabled={!selected || assigning}
          sx={{ height: 36, px: "18px", borderRadius: "10px", border: 0, bgcolor: (!selected || assigning) ? D.surface3 : tPrimary, color: (!selected || assigning) ? D.ink3 : "#fff", fontFamily: "inherit", fontSize: "13px", fontWeight: 700, cursor: (!selected || assigning) ? "default" : "pointer", display: "inline-flex", alignItems: "center", gap: "7px", boxShadow: (!selected || assigning) ? "none" : `0 4px 12px -4px ${alpha(tPrimary, 0.45)}`, transition: "all .13s", "&:hover:not(:disabled)": { bgcolor: tPrimaryDk } }}>
          {assigning ? <><CircularProgress size={12} sx={{ color: "inherit" }} /> Adding…</> : `Add ${cfg.label}`}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
